import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';

const POSTIZ_PRODUCTION_URL = 'https://postiz.localtry.com';
const POSTIZ_LOCAL_URL = 'http://localhost:4007';

const Page = styled.div`
  min-height: 100vh;
  color: white;
  background:
    radial-gradient(circle at 16% 8%, rgba(24, 230, 167, 0.18), transparent 32%),
    radial-gradient(circle at 82% 18%, rgba(255, 179, 71, 0.16), transparent 28%),
    radial-gradient(circle at 70% 76%, rgba(255, 90, 122, 0.12), transparent 34%),
    #05080d;
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  padding: 190px 7vw 90px;

  @media (max-width: 768px) {
    padding: 150px 20px 60px;
  }
`;

const HeroGrid = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.95fr);
  gap: 56px;
  align-items: center;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid rgba(24, 230, 167, 0.35);
  border-radius: 999px;
  background: rgba(24, 230, 167, 0.08);
  color: #aaf8df;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
  margin-bottom: 22px;
`;

const Title = styled(motion.h1)`
  font-size: clamp(42px, 7vw, 82px);
  line-height: 0.96;
  font-weight: 900;
  max-width: 780px;
  background: linear-gradient(90deg, #18e6a7 0%, #ffb347 58%, #ff5a7a 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 24px;
`;

const Copy = styled(motion.p)`
  color: rgba(255, 255, 255, 0.78);
  font-size: clamp(17px, 2.2vw, 22px);
  line-height: 1.55;
  max-width: 720px;
  margin-bottom: 34px;
`;

const ActionRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

const PrimaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 24px;
  border-radius: 999px;
  background: linear-gradient(90deg, #18e6a7 0%, #ffb347 100%);
  color: #07100d;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 16px 36px rgba(24, 230, 167, 0.24);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 42px rgba(24, 230, 167, 0.3);
  }
`;

const SecondaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: white;
  font-weight: 700;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: rgba(255, 179, 71, 0.55);
    background: rgba(255, 179, 71, 0.1);
  }
`;

const Console = styled(motion.div)`
  position: relative;
  min-height: 470px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: linear-gradient(150deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.035));
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.38);
  overflow: hidden;

  @media (max-width: 480px) {
    min-height: 390px;
  }
`;

const ConsoleTop = styled.div`
  height: 52px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  background: rgba(0, 0, 0, 0.22);

  span {
    width: 9px;
    height: 9px;
    border-radius: 99px;
    background: #ff5a7a;
  }

  span:nth-child(2) {
    background: #ffb347;
  }

  span:nth-child(3) {
    background: #18e6a7;
  }
`;

const Calendar = styled.div`
  padding: 22px;
  display: grid;
  gap: 14px;
`;

const ChannelRow = styled.div<{ $accent: string }>`
  display: grid;
  grid-template-columns: 108px 1fr 78px;
  gap: 14px;
  align-items: center;
  padding: 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.055);
  border-left: 3px solid ${props => props.$accent};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Channel = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.86);
`;

const PostLine = styled.div`
  height: 11px;
  border-radius: 99px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.72), rgba(255, 255, 255, 0.16));
`;

const Status = styled.div`
  justify-self: end;
  color: #18e6a7;
  font-size: 12px;
  font-weight: 800;

  @media (max-width: 480px) {
    justify-self: start;
  }
`;

const Section = styled.section`
  padding: 28px 7vw 105px;
`;

const Inner = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(30px, 4vw, 48px);
  line-height: 1.08;
  margin-bottom: 18px;
`;

const SectionIntro = styled(motion.p)`
  max-width: 820px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  line-height: 1.65;
  margin-bottom: 34px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  min-height: 210px;
  padding: 24px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.045);
`;

const FeatureLabel = styled.div`
  color: #ffb347;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
  margin-bottom: 14px;
`;

const FeatureTitle = styled.h3`
  font-size: 23px;
  line-height: 1.18;
  margin-bottom: 12px;
`;

const FeatureCopy = styled.p`
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.58;
`;

const SocialPublisherPage: React.FC = () => {
  return (
    <Page>
      <Header />
      <Hero>
        <HeroGrid>
          <div>
            <Eyebrow
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              MarketNest command center
            </Eyebrow>
            <Title
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.08 }}
            >
              Social publishing for every client channel.
            </Title>
            <Copy
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.16 }}
            >
              Plan, approve, schedule, and publish YouTube, TikTok, Instagram, Facebook, LinkedIn, and more from one branded MarketNest workflow.
            </Copy>
            <ActionRow
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.24 }}
            >
              <PrimaryAction href={POSTIZ_PRODUCTION_URL}>Open Publisher</PrimaryAction>
              <SecondaryAction href={POSTIZ_LOCAL_URL}>Open Local Admin</SecondaryAction>
            </ActionRow>
          </div>

          <Console
            initial={{ opacity: 0, scale: 0.96, y: 22 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.18 }}
          >
            <ConsoleTop>
              <span />
              <span />
              <span />
            </ConsoleTop>
            <Calendar>
              <ChannelRow $accent="#18E6A7">
                <Channel>YouTube</Channel>
                <PostLine />
                <Status>Ready</Status>
              </ChannelRow>
              <ChannelRow $accent="#FF5A7A">
                <Channel>TikTok</Channel>
                <PostLine />
                <Status>Queued</Status>
              </ChannelRow>
              <ChannelRow $accent="#FFB347">
                <Channel>Instagram</Channel>
                <PostLine />
                <Status>Review</Status>
              </ChannelRow>
              <ChannelRow $accent="#18E6A7">
                <Channel>LinkedIn</Channel>
                <PostLine />
                <Status>Live</Status>
              </ChannelRow>
              <ChannelRow $accent="#FF5A7A">
                <Channel>Facebook</Channel>
                <PostLine />
                <Status>Draft</Status>
              </ChannelRow>
            </Calendar>
          </Console>
        </HeroGrid>
      </Hero>

      <Section>
        <Inner>
          <SectionTitle
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
          >
            Built for agency work, not one-off posting.
          </SectionTitle>
          <SectionIntro
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.08 }}
          >
            The publisher gives MarketNest a repeatable system for managing client content calendars while still letting each brand keep its own voice, avatar, platform mix, and posting cadence.
          </SectionIntro>
          <FeatureGrid>
            <FeatureCard
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
            >
              <FeatureLabel>01</FeatureLabel>
              <FeatureTitle>Multi-channel scheduling</FeatureTitle>
              <FeatureCopy>Queue posts, videos, captions, and campaign assets across the channels each client actually uses.</FeatureCopy>
            </FeatureCard>
            <FeatureCard
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: 0.08 }}
            >
              <FeatureLabel>02</FeatureLabel>
              <FeatureTitle>Client-by-client systems</FeatureTitle>
              <FeatureCopy>Keep each brand separated, organized, and consistent so workflows can scale without losing the details.</FeatureCopy>
            </FeatureCard>
            <FeatureCard
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: 0.16 }}
            >
              <FeatureLabel>03</FeatureLabel>
              <FeatureTitle>AI-assisted production</FeatureTitle>
              <FeatureCopy>Connect the publishing calendar to MarketNest's video, scripting, and content workflows as they mature.</FeatureCopy>
            </FeatureCard>
          </FeatureGrid>
        </Inner>
      </Section>
      <Footer />
    </Page>
  );
};

export default SocialPublisherPage;
